export type TimeSlot = { label: string; value: string };

export class DateUtils {
  public static dateInISOFormat(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  public static getDateTodayISO(): string {
    return this.dateInISOFormat(new Date());
  }

  public static getDateTodayMinusDays(days: number): string {
    const date = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
    return this.dateInISOFormat(date);
  }

  public static getDateTodayPlusDays(days: number): string {
    const date = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
    return this.dateInISOFormat(date);
  }

  public static addDaysToTargetDate(date: Date, daysToAdd: number): string {
    const targetDate: Date = new Date(date);
    targetDate.setDate(date.getDate() + daysToAdd);

    return this.dateInISOFormat(targetDate);
  }

  public static getShortDateFromDate(
    date: Date,
    year: boolean = false,
  ): string {
    const newDate = new Date(date);
    return new Intl.DateTimeFormat('no-NB', {
      month: 'short',
      day: 'numeric',
      ...(year && { year: 'numeric' }),
    }).format(newDate);
  }

  public static getMonthFromCurrent = (
    monthNumber: number,
    customDate?: Date,
  ) => {
    let date = customDate || new Date();
    date.setUTCMonth(date.getUTCMonth() - monthNumber);

    return date;
  };

  public static getClockTimeFromDate(date: Date, seconds?: boolean): string {
    const newDate = new Date(date);
    return new Intl.DateTimeFormat('no-NB', {
      hour: 'numeric',
      minute: 'numeric',
      ...(seconds && { second: 'numeric' }),
      timeZone: 'Europe/Oslo',
    }).format(newDate);
  }

  public static getTimeFromDate(date: Date) {
    const time = date.toLocaleTimeString('nb-NO', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Oslo',
    });
    return time;
  }

  public static generateTimeSlots = (
    startHour: number,
    endHour: number,
    intervalMinutes: number,
  ): TimeSlot[] => {
    const times: TimeSlot[] = [];
    const date = new Date();

    date.setHours(startHour, 0, 0, 0);

    while (date.getHours() < endHour + 1) {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');

      const label = `${hours}:${minutes}`;

      const localTime = new Date(
        date.toLocaleString('en-US', { timeZone: 'Europe/Oslo' }),
      );

      const value = localTime.toTimeString().slice(0, 8);

      times.push({ label, value });

      date.setMinutes(date.getMinutes() + intervalMinutes);
    }

    return times;
  };

  public static getTimeISOFromDate(date: Date) {
    const time = date.toLocaleTimeString('nb-NO', {
      timeZone: 'Europe/Oslo',
    });
    return time;
  }

  public static getDateMinusDays(date: Date, days: number): Date {
    const newDate = new Date(date);
    newDate.setDate(date.getDate() - days);
    return newDate;
  }

  public static getDatePlusDays(date: Date, days: number): Date {
    const newDate = new Date(date);
    newDate.setDate(date.getDate() + days);
    return newDate;
  }

  public static DateIsAfterToday(date: Date): boolean {
    const today = new Date();
    return date > today;
  }

  /**
   * Calculates the next occurrence of a given weekday from today
   * @param weekday - The weekday name (e.g., 'monday', 'friday')
   * @returns Formatted date string in Norwegian format (e.g., 'fredag 21. februar')
   */
  public static calculateNextWeekday(weekday: string): string {
    const weekdayMap: Record<string, number> = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    };

    const today = new Date();
    const targetDay = weekdayMap[weekday];
    const currentDay = today.getDay();
    let daysUntil = targetDay - currentDay;
    if (daysUntil <= 0) daysUntil += 7;

    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + daysUntil);

    return nextDate.toLocaleDateString('nb-NO', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  }
}
